import React, { useState, useCallback, useEffect } from 'react';
import { Stack, Loading } from '@moda/om';
import { loadAmazonWidgetScript } from '../../../lib/loadScript';
import { reportError } from '../../../lib/reportError';
import { isLoggedInOnAmazon, logoutOnAmazon, waitForAmazonWidgetScript } from '../../../lib/amazon';
import { useErrorModal } from '../../../hooks/useErrorModal';
import { useSocialAccountConnection } from '../../../hooks/useSocialAccountConnection';
import { SocialAuthProvider } from '../../../hooks/useSocialAuth';
import { AmazonAddressWidget } from '../AmazonAddressWidget';
import { AmazonWalletWidget } from '../AmazonWalletWidget';
import { AmazonConsentWidget } from '../AmazonConsentWidget';
import './AmazonShippingAndPaymentWidgets.scss';

type Props = {
  onReady?: (billingAgreementId: string) => void;
  onConsentStatusChange?: (checked: boolean) => void;
};

export const AmazonShippingAndPaymentWidgets: React.FC<Props> = ({
  onReady,
  onConsentStatusChange
}) => {
  const { showErrorModal } = useErrorModal();
  const { handleConnect } = useSocialAccountConnection(SocialAuthProvider.Amazon);
  const [isReady, setIsReady] = useState(false);
  const [billingAgreementId, setBillingAgreementId] = useState('');

  const handleAddressWidgetReady = useCallback(
    (billingAgreementId: string) => {
      setBillingAgreementId(billingAgreementId);
      onReady?.(billingAgreementId);
    },
    [setBillingAgreementId, onReady]
  );

  const handleAddressWidgetError = useCallback(() => {
    logoutOnAmazon();
    setIsReady(false);
  }, [setIsReady]);

  useEffect(() => {
    const connectToAmazon = async () => {
      try {
        await waitForAmazonWidgetScript();
        const isLoggedIn = await isLoggedInOnAmazon();
        if (!isLoggedIn) await handleConnect();
        setIsReady(true);
      } catch (error) {
        reportError(error);
        showErrorModal();
      }
    };

    if (!window.OffAmazonPayments) loadAmazonWidgetScript();
    if (!isReady) connectToAmazon();
  }, [handleConnect, showErrorModal, isReady]);

  return (
    <Stack space={4} className="AmazonShippingAndPaymentWidgets">
      <div className="AmazonShippingAndPaymentWidgets__top">
        {!isReady && <Loading />}
        <div className="AmazonShippingAndPaymentWidgets__widget">
          {isReady && (
            <AmazonAddressWidget
              onReady={handleAddressWidgetReady}
              onError={handleAddressWidgetError}
            />
          )}
        </div>
        <div className="AmazonShippingAndPaymentWidgets__widget">
          {billingAgreementId && <AmazonWalletWidget billingAgreementId={billingAgreementId} />}
        </div>
      </div>
      {billingAgreementId && (
        <AmazonConsentWidget
          billingAgreementId={billingAgreementId}
          onChange={onConsentStatusChange}
        />
      )}
    </Stack>
  );
};
